import { LOCKSTEP, type ServerMessage } from '@orb/protocol';
import type { Action, Input } from '@orb/sim';

export const TICK_MS = 1000 / LOCKSTEP.tickRate;
/** Buffered ticks beyond which the client fast-forwards (5 s), and its ticks per loop step. */
export const FAR_BEHIND = 300;
export const FAST_FORWARD = 600;
/** Inputs already played are kept this long (20 s), so a snapshot taken a little in the past can be caught up from. */
export const REPLAY_WINDOW = 1200;

/** Where the outgoing actions and checksums go (a Link, or a fake in tests). */
export interface Outbox { send(m: { type: 'input'; tick: number; action: Action } | { type: 'checksum'; tick: number; hash: number }): boolean }

/**
 * The client half of lockstep. Turns from the server fill a buffer; the simulation may run a tick
 * only once its turn has arrived (otherwise it waits). Own actions go out stamped `delay` ticks ahead,
 * and take effect when they come back in a turn — on every client at the same tick.
 */
export class Lockstep {
  /** First tick not yet covered by a received turn. */
  received: number;
  /** Ticks of input delay currently used. */
  delay: number = LOCKSTEP.inputDelay;
  /** A turn that did not start where the previous one ended (lost connection): the game must resync (stage 7). */
  gap = false;
  private readonly byTick = new Map<number, Input[]>();
  private readonly played = new Map<number, Input[]>();
  private readonly out: Outbox;
  private steps = 0;

  constructor(out: Outbox, startTick = 0) { this.out = out; this.received = startTick; }

  receive(m: Extract<ServerMessage, { type: 'turn' }>) {
    if (m.fromTick !== this.received) { if (m.fromTick > this.received) this.gap = true; return; }
    for (const i of m.inputs) {
      const t = m.fromTick + i.offset;
      let list = this.byTick.get(t);
      if (!list) this.byTick.set(t, (list = []));
      list.push({ player: i.seat, action: i.action });
    }
    this.received = m.fromTick + m.ticks;
  }

  /**
   * The sim jumped to `tick` (a restored snapshot): turns before it are moot, and turns from it on
   * are what comes next.
   */
  restoreAt(tick: number) {
    for (const t of this.byTick.keys()) if (t < tick) this.byTick.delete(t);
    // ticks already played after the snapshot's will run again
    for (const [t, list] of this.played) if (t >= tick) this.byTick.set(t, list);
    this.played.clear();
    if (this.received < tick) this.received = tick;
    this.gap = false;
  }

  /** Whether a snapshot of `tick` can still be caught up from (its later inputs are remembered). */
  canRestore(tick: number, current: number) { return tick >= current || current - tick <= REPLAY_WINDOW; }

  /** Ticks buffered ahead of `tick`. */
  buffered(tick: number) { return this.received - tick; }
  ready(tick: number) { return tick < this.received; }

  /** The inputs for `tick` (call once, when stepping it). */
  take(tick: number): Input[] {
    const list = this.byTick.get(tick);
    if (this.played.size && tick % 60 === 0) for (const t of this.played.keys()) if (t < tick - REPLAY_WINDOW) this.played.delete(t);
    if (!list) return [];
    this.byTick.delete(tick);
    this.played.set(tick, list);
    return list;
  }

  /**
   * How many ticks to run on this 60 Hz step: 0 while the next turn is missing (wait, never guess),
   * normally 1, and 2 on every other step while somewhat behind — a gentle catch-up after a network
   * hiccup, so the extra delay it left does not stay for the rest of the match. After a long absence
   * (a hidden tab, a dropped connection, a reload replaying the match) it fast-forwards up to
   * FAST_FORWARD ticks a step: the sim costs ~10 µs a tick (600 ≈ 6 ms), and the player would
   * otherwise act seconds in the past.
   */
  ticksThisStep(tick: number, rttVar = 0): number {
    const have = this.buffered(tick);
    if (have <= 0) return 0;
    this.steps++;
    const target = LOCKSTEP.ticksPerTurn + Math.ceil((2 * rttVar) / TICK_MS);
    if (have > FAR_BEHIND) return Math.min(FAST_FORWARD, have - target);
    return have > target + 2 * LOCKSTEP.ticksPerTurn && this.steps % 2 === 0 ? 2 : 1;
  }

  /** Send an own action to run `delay` ticks after the current one. */
  send(action: Action, tick: number) { return this.out.send({ type: 'input', tick: tick + this.delay, action }); }

  /** After stepping to `tick` (state = tick ticks done): report the checksum on the agreed beat. */
  stepped(tick: number, checksum: () => number) {
    if (tick > 0 && tick % LOCKSTEP.checksumEvery === 0) this.out.send({ type: 'checksum', tick, hash: checksum() });
  }

  /**
   * Fit the delay to the connection: an action must reach the server before it sends the turn
   * holding that tick — about one round trip plus a turn, with a margin for jitter. Moves one tick
   * per call, so the feel changes smoothly.
   */
  adapt(rtt: number, rttVar: number) {
    if (Number.isNaN(rtt)) return;
    const want = Math.min(LOCKSTEP.maxInputDelay, Math.max(LOCKSTEP.minInputDelay, Math.ceil((rtt + 2 * rttVar) / TICK_MS) + LOCKSTEP.ticksPerTurn));
    this.delay += Math.sign(want - this.delay);
  }
}
